<template>
    <div ref="sidewindow" :key="forceUpdate" class="md:ml-4 w-full md:max-w-[40vw]  max-w-[700px]"
        :class="iframe ? 'md:!w-[300px]' : ''">

        <div
            class="h-10 -mb-6 z-1 md:hidden pointer-events-none bg-gradient-to-t from-neutral-900/20 from-0% to-transparent to-100%">
        </div>
        <div
            class="sticky -mb-6 md:hidden top-2 z-[7] bg-white dark:bg-neutral-800/50 border border-gray-100/30 dark:border-neutral-800/10 left-[50%] rounded-full w-[50%] h-2 -translate-x-[50%]">
        </div>
        <div
            class="w-full min-h-[95vh] z-[6] p-3 md:z-[4] md:min-h-[75vh] md:h-full rounded-3xl bg-white/70 dark:bg-neutral-900/20 backdrop-blur-xl ring-1 !ring-black/10 dark:!ring-neutral-300/10 dark:text-white">
            <template v-if="loading || !selectedLocationData">
                <div class="relative animate-pulse">
                    <div class="w-full h-48 md:h-64 bg-gray-300 dark:bg-neutral-700 rounded-2xl" :class="iframe ? 'md:!h-32' : ''"></div>
                    <div class="bg-gray-200 dark:bg-neutral-600 absolute bottom-0 right-4 translate-y-[50%] 
               w-28 h-28 rounded-full border-2 border-white shadow-xl" :class="iframe ? '!w-16 !h-16' : ''">
                    </div>
                </div>

                <div class="flex py-4 flex-col" :class="iframe ? '!pt-2' : ''">
                    <div class="px-4 py-1 flex flex-col max-w-[70%] animate-pulse">
                        <div class="h-6 w-40 bg-gray-300 dark:bg-neutral-700 rounded mb-2"></div>
                        <div class="h-3 w-32 bg-gray-200 dark:bg-neutral-600 rounded mb-4"></div>
                        <div class="flex gap-2 items-center my-2">
                            <div class="h-4 w-4 bg-gray-300 dark:bg-neutral-700 rounded"></div>
                            <div class="h-3 w-20 bg-gray-200 dark:bg-neutral-600 rounded"></div>
                        </div>
                    </div>


                    <div class="px-4 flex gap-2 mb-2 w-full overflow-x-scroll py-1 scrollbar-hide animate-pulse">
                        <div class="h-7 w-20 bg-gray-200 dark:bg-neutral-700 rounded-full"></div>
                        <div class="h-7 w-16 bg-gray-200 dark:bg-neutral-700 rounded-full"></div>
                        <div class="h-7 w-24 bg-gray-200 dark:bg-neutral-700 rounded-full"></div>
                    </div>

                    <div class="mx-4 my-1">
                        <div class="w-full h-12 bg-gray-300 dark:bg-neutral-700 rounded-lg animate-pulse"></div>
                    </div>

                    <div class="px-4 mt-4 space-y-2 animate-pulse">
                        <div class="h-3 w-full bg-gray-200 dark:bg-neutral-700 rounded"></div>
                        <div class="h-3 w-[90%] bg-gray-200 dark:bg-neutral-700 rounded"></div>
                        <div class="h-3 w-[80%] bg-gray-200 dark:bg-neutral-700 rounded"></div>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="relative">
                    <img v-if="selectedLocationData?.image_link" :src="selectedLocationData.image_link"
                        class="w-full h-48 md:h-64  bg-gray-400 object-cover rounded-2xl "
                        :class="iframe ? 'md:!h-32' : ''">
                    <div v-else class="w-full h-48 md:h-64 bg-gray-400 rounded-2xl" :class="iframe ? 'md:!h-32' : ''">
                    </div>
                    <template v-if="selectedLocationData?.logo_link">
                        <img :src="selectedLocationData.logo_link"
                            class="bg-white absolute bottom-0 right-4 translate-y-[50%] w-28 h-28 object-contain rounded-full border-2 border-white shadow-xl"
                            :class="iframe ? '!w-16 !h-16' : ''">
                    </template>
                </div>
                <div class="flex py-4 flex-col" :class="iframe ? '!pt-2' : ''">
                    <div class="px-4 py-1 flex flex-col max-w-[70%]">
                        <span class="text-2xl font-bold" :class="iframe ? '!text-xl' : ''"> {{
                            getTranslated(selectedLocationData?.name) }} </span>
                        <span class="text-xs text-gray-500">
                            {{ selectedLocationData?.street }}, {{ selectedLocationData?.postcode }} {{
                                selectedLocationData?.city
                            }}
                        </span>
                        <div class="shrink-0 flex gap-2 items-center mt-5 mb-2" :class="iframe? '!mt-3': ''">
                            <div :class="`${meta(selectedLocationData?.type).color}`">
                                <svg-vue class="h-4" :icon="meta(selectedLocationData?.type).icon" />
                            </div>
                            <div :class="`${meta(selectedLocationData?.type).color} text-xs`">
                                {{ meta(selectedLocationData?.type).label }}
                            </div>
                        </div>
                    </div>
                    <div v-if="selectedLocationData?.platform_categories && selectedLocationData?.platform_categories.length" class="px-4 select-none flex gap-2 mb-2 w-full overflow-x-scroll py-1 scrollbar-hide">
                        <a v-for="category in selectedLocationData?.platform_categories" :key="category.slug"
                            :href="$baseUrl + `/categories/${category.slug}`" :class="[
                                `category_${category.slug}`,
                                'py-2 px-3 bg-neutral-100/50 nowrap dark:bg-neutral-700/50 backdrop-blur-xl rounded-full border border-neutral-300 dark:!border-gray-500 dark:text-white text-xs capitalize flex gap-1 items-center justify-between hover:scale-110 active:scale-90 transition'
                            ]">
                            <div v-if="category.iconSvg" class="w-4 h-4" v-html="category.iconSvg"></div>
                            <span v-if="category.iconUrl" class="w-0 h-0 overflow-hidden">{{ category.iconUrl }}</span>
                            <span class="whitespace-nowrap">{{ getTranslated(category.name) }}</span>
                        </a>
                    </div>
                    <a :href="$baseUrl + '/locations/' + selectedLocationData?.slug" target="_blank"
                        class="mx-4 block my-1 bg-gradient shadow-xl px-5 py-3 md:py-5 mb-2 rounded-lg cursor-pointer hover:scale-95 transition duration-300 active:scale-75 text-white text-center"
                        :class="iframe ? '!py-3' : ''">
                        <span>
                            {{ $t('See location') }}
                        </span>
                    </a>
                    <span class="px-4 text-base dark:text-white mt-4"
                        v-html="getTranslated(selectedLocationData?.description)"></span>
                </div>
            </template>
        </div>
    </div>
</template>


<script>
import axios from "axios";

import 'maplibre-gl/dist/maplibre-gl.css';
import { useTypeMeta } from '../../helpers/useTypeMeta'
const { debounce } = require('lodash');

export default {
    name: 'SelectedLocation',

    props: {
        iframe: {
            type: Boolean,
            default: false,
        },
        selected: {
            type: String,
            default: '',
        },
        slug: String,
    },

    data() {
        return {
            selectedLocationData: null,
            forceUpdate: 0,
            loading: false,
            error: null,
        };
    },
    watch: {
        // whenever the selected slug changes → reload data
        selected: {
            immediate: true,
            handler(newVal) {
                if (!newVal) {
                    this.selectedLocationData = null;
                    return;
                }
                this.fetchSelectedLocation(newVal);
            }
        }
    },
    methods: {
        meta(type) {
            return useTypeMeta(type)
        },
        getTranslated(item, lang = 'en') {
            if (!item) return '';

            const translations = item[lang] || item['en'];
            return translations;
        },
        async fetchSelectedLocation(slug) {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get(`/resources/locations/${slug}`);
                this.selectedLocationData = response.data;

                // force component re-render (layout recalculates)
                this.forceUpdate++;

            } catch (err) {
                console.error("Failed to load location:", err);
                this.error = "Failed to load location.";
            }

            this.loading = false;
        },
    },
    mounted() {
        this.fetchSelectedLocation(this.slug);
    }
};
</script>